import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Star } from "lucide-react";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_accommodation-plus/artifacts/1v4qzw6c_587709409.jpg";

// Short rotating 5-star reviews (multilingual). Tone: real, brief, evocative.
const REVIEWS = {
  it: [
    { text: "Accoglienza calorosa, camere pulite e silenziose. Tornerò sicuramente.", author: "Giulia · Milano" },
    { text: "Vista mare meravigliosa e colazione fatta in casa. Esperienza perfetta.", author: "Marco · Torino" },
    { text: "Un piccolo gioiello a Reggio Calabria. Lo staff sa farti sentire a casa.", author: "Sofia · Roma" },
    { text: "Posizione comodissima, parcheggio incluso, prezzo ottimo. Promosso!", author: "Luca · Bologna" },
    { text: "Pulito, curato, profumato. Si vede che amano quello che fanno.", author: "Anna · Firenze" },
  ],
  en: [
    { text: "Warm welcome, spotless and quiet rooms. We will definitely be back.", author: "James · London" },
    { text: "Lovely sea view and homemade breakfast. A wonderful experience.", author: "Emma · Berlin" },
    { text: "A hidden gem in Reggio Calabria. The staff truly makes you feel at home.", author: "Sara · Madrid" },
    { text: "Great location, parking included, top value for money.", author: "Tom · Dublin" },
    { text: "Clean, lovingly curated. You can tell they love what they do.", author: "Lisa · Amsterdam" },
  ],
  de: [
    { text: "Herzliche Aufnahme, ruhige und blitzsaubere Zimmer. Wir kommen wieder!", author: "Klaus · München" },
    { text: "Wunderbarer Meerblick, hausgemachtes Frühstück. Tolles Erlebnis.", author: "Anja · Berlin" },
    { text: "Ein kleines Juwel in Reggio Calabria. Das Personal ist ein Traum.", author: "Petra · Wien" },
    { text: "Top Lage, Parkplatz inklusive, ausgezeichnetes Preis-Leistungs-Verhältnis.", author: "Hans · Hamburg" },
    { text: "Sauber, mit Liebe gepflegt. Man spürt die Leidenschaft.", author: "Eva · Zürich" },
  ],
  fr: [
    { text: "Accueil chaleureux, chambres impeccables et silencieuses. On reviendra.", author: "Marie · Paris" },
    { text: "Magnifique vue sur mer et petit-déjeuner maison. Expérience parfaite.", author: "Pierre · Lyon" },
    { text: "Un petit bijou à Reggio Calabria. Le personnel vous met à l'aise.", author: "Camille · Marseille" },
    { text: "Position idéale, parking inclus, excellent rapport qualité-prix.", author: "Antoine · Bordeaux" },
    { text: "Propre, soigné. On sent qu'ils aiment ce qu'ils font.", author: "Élise · Genève" },
  ],
};

const About = () => {
  const { t, lang } = useLang();
  const reviews = useMemo(() => REVIEWS[lang] || REVIEWS.it, [lang]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(id);
  }, [reviews.length]);

  const cur = reviews[idx];

  return (
    <section id="about" className="py-20 md:py-28 bg-[#FDFBF7]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="relative">
          <img src={ABOUT_IMG} alt="About B&B Emmanuel" className="rounded-2xl w-full aspect-[4/5] object-cover shadow-lg" />

          {/* 5-star rotating reviews badge — replaces the old static "Casa lontano da casa" */}
          <div
            className="absolute -bottom-6 -right-6 hidden md:block bg-white text-[#1E3A5F] p-5 rounded-2xl max-w-[260px] shadow-2xl border border-[#E8E2D9]"
            data-testid="about-reviews-badge"
          >
            <div className="flex items-center gap-0.5 mb-2" aria-label="5 stelle">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#E3A857] text-[#E3A857]" />
              ))}
            </div>
            <p
              key={idx}
              className="font-display text-base leading-snug italic mb-1.5 animate-fadeUp"
              data-testid="about-review-text"
            >
              "{cur.text}"
            </p>
            <p className="text-[11px] text-[#635F59] uppercase tracking-wider">{cur.author}</p>
            <div className="flex justify-center gap-1 mt-2.5" aria-hidden>
              {reviews.map((r, i) => (
                <span
                  key={`dot-${r?.author || r?.text?.slice(0, 12) || i}`}
                  className={`block w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-[#AC5D45] w-3" : "bg-[#E8E2D9]"}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div>
          <p className="text-overline mb-3">{t.about.overline}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#1E3A5F] leading-tight mb-6">{t.about.title}</h2>
          <div className="bb-divider mb-6" />
          <p className="text-[#2D2926] leading-relaxed mb-4 text-base">{t.about.p1}</p>
          <p className="text-[#635F59] leading-relaxed text-base">{t.about.p2}</p>

          <div className="grid grid-cols-3 gap-6 mt-10 pt-8 border-t border-[#E8E2D9]">
            {[
              { id: "stat1", n: t.about.stat1_n, l: t.about.stat1_l },
              { id: "stat2", n: t.about.stat2_n, l: t.about.stat2_l },
              { id: "stat3", n: t.about.stat3_n, l: t.about.stat3_l },
            ].map((s, i) => (
              <div key={s.id} data-testid={`about-stat-${i}`}>
                <div className="font-display text-4xl text-[#AC5D45] font-semibold">{s.n}</div>
                <div className="text-xs text-[#635F59] tracking-wider uppercase mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
